import type { Equipped, MatchRewards, Profile } from './profile';
import type { EncodedTerrain } from './terrainCodec';
import type { GameMode, MatchEvent, PlayerOutcome, WeaponId } from './types';

/**
 * A map to play: one of the Raft Wars arenas below (authored by hand), one of the war maps
 * (`war:...`, laid out after the stages of the Territory War games), a catalogue map (`cat:...`,
 * generated from a spec the bot tests approved), an inline generated spec
 * (`gen:family:seed:k=v,...`), or RANDOM_MAP, which the server replaces with a pick from the
 * lobby's map pool when the match starts. The client resolves the rest (client/src/game/maps.ts);
 * unknown ids fall back to the first arena.
 */
export type MapId = string;
/** The hand-authored arenas: Raft Wars scenes, all on water. */
export type AuthoredMapId = 'treeisle' | 'port' | 'marina' | 'beach' | 'waterpark' | 'pirate' | 'liner' | 'digsite' | 'bigtop';
export const MAP_IDS: readonly AuthoredMapId[] = ['treeisle', 'port', 'marina', 'beach', 'waterpark', 'pirate', 'liner', 'digsite', 'bigtop'];
export const MAP_NAMES: Record<AuthoredMapId, string> = {
  treeisle: 'Tree Island',
  port: 'Container Port',
  marina: 'Marina',
  beach: 'Lifeguard Beach',
  waterpark: 'Waterpark',
  pirate: 'Pirate Cove',
  liner: 'Ocean Liner',
  digsite: 'Beach Dig',
  bigtop: 'Circus Beach',
};
/**
 * The war maps, by id: land arenas laid out after Territory War 1, 2 and 3 stages (the shapes
 * live in client/src/game/warmaps.ts; this list is what the server and the lobby know).
 */
export const WAR_MAP_NAMES: Record<string, string> = {
  'war:tiers': 'Tiers',
  'war:watch': 'Watchtower',
  'war:crossing': 'Crossing',
  'war:pyramid': 'Pyramid',
  'war:hollow': 'Hollow',
  'war:gate': 'Gatehouse',
  'war:twin': 'Twin Towers',
  'war:pillars': 'Pillars',
  'war:bunkers': 'Bunkers',
  'war:ridge': 'Ridge',
  'war:steps': 'Steps',
  'war:yard': 'Yard',
  'war:keep': 'Keep',
  'war:summit': 'Summit',
  'war:causeway': 'Causeway',
  'war:hills': 'Twin Hills',
  'war:highlands': 'Highlands',
  'war:plateaus': 'Plateaus',
  'war:citadel': 'Citadel',
  'war:badlands': 'Badlands',
};
export const WAR_MAP_IDS: readonly string[] = Object.keys(WAR_MAP_NAMES);
/** The map setting that means "the server picks one from the pool when the match starts". */
export const RANDOM_MAP = 'random';
/** What a random pick draws from unless the lobby narrows it: every arena and war map. */
export const DEFAULT_MAP_POOL: readonly string[] = [...MAP_IDS, ...WAR_MAP_IDS];
/** Most ids a lobby's map pool may hold (the catalogue alone is over a hundred). */
export const MAX_MAP_POOL = 400;

export type TurnSeconds = 15 | 30 | 45 | 60;

/**
 * Versus only: how much money everyone gets. Upper class pays three times the coins (starting
 * purse, round income and bounties alike); broke pays half. Prices never change.
 */
export type Wealth = 'normal' | 'upper' | 'broke';
export const WEALTHS: readonly Wealth[] = ['broke', 'normal', 'upper'];
export const WEALTH_NAMES: Record<Wealth, string> = { broke: 'Broke', normal: 'Normal', upper: 'Upper class' };
export const WEALTH_MULTIPLIER: Record<Wealth, number> = { broke: 0.5, normal: 1, upper: 3 };

export interface MatchSettings {
  mode: GameMode;
  map: MapId;
  turnSeconds: TurnSeconds;
  unitsPerTeam: 1 | 2 | 3 | 4;
  /** Number of teams in Versus (players are spread across them). */
  /** Number of teams in Versus and Brawl, 2 to MAX_TEAMS; with one player per team it is free-for-all. */
  teams: number;
  /** Rounds within which a win counts as "quick" and earns the second campaign star. */
  parRounds: number;
  /** Off by default: Territory War and Raft Wars have no wind. */
  wind: boolean;
  /** Co-op: how many AI waves to survive. */
  waves: number;
  /** Versus, Brawl and Co-op: rounds needed to win are ceil(bestOf / 2). The campaign always plays one. */
  bestOf: 1 | 3 | 5;
  /** Versus and Co-op: 3x the money (upper) or half (broke). Absent means normal. */
  wealth?: Wealth;
  /** The map ids a RANDOM_MAP pick may land on. Absent means DEFAULT_MAP_POOL. */
  mapPool?: string[];
  campaignLevelId?: string;
}

export const DEFAULT_SETTINGS: MatchSettings = {
  mode: 'versus',
  map: RANDOM_MAP,
  turnSeconds: 30,
  /** Two standard moles; the rest of a crew is bought in the buy phase. */
  unitsPerTeam: 2,
  teams: 2,
  parRounds: 6,
  wind: false,
  waves: 3,
  bestOf: 3,
  wealth: 'normal',
  mapPool: [...DEFAULT_MAP_POOL],
};

/** A concrete map for a match: a random setting becomes a pick from the pool (or the whole default pool). */
export function pickMap(settings: MatchSettings, random: () => number = Math.random): MapId {
  if (settings.map !== RANDOM_MAP) return settings.map;
  const pool = (settings.mapPool ?? []).filter((id) => id !== RANDOM_MAP);
  const from = pool.length ? pool : DEFAULT_MAP_POOL;
  return from[Math.floor(random() * from.length)] ?? MAP_IDS[0]!;
}

/** Most teams a lobby can split into: every player on their own side. */
export const MAX_TEAMS = 8;
/** Hard caps that keep a 25-person call sane. */
export const MAX_PLAYERS: Record<GameMode, number> = { versus: 8, brawl: 8, coop: 4, campaign: 1 };

export interface LobbyPlayer {
  id: string;
  name: string;
  avatarUrl?: string;
  team: number;
  ready: boolean;
  spectator: boolean;
  connected: boolean;
  level: number;
  equipped: Equipped;
}

export type LobbyPhase = 'lobby' | 'match' | 'results';

export interface LobbyState {
  instanceId: string;
  hostId: string;
  phase: LobbyPhase;
  settings: MatchSettings;
  players: LobbyPlayer[];
  /** The match in progress, so someone who joins mid-match can spectate it. */
  match?: MatchStart;
}

/** Everything a client needs to build the same match the host is simulating. */
export interface MatchPlayer {
  id: string;
  name: string;
  avatarUrl?: string;
  team: number;
  equipped: Equipped;
  /** Effective perk ids. Always empty in Versus. */
  perks: string[];
  isBot: boolean;
}

export interface MatchStart {
  seed: number;
  settings: MatchSettings;
  hostId: string;
  players: MatchPlayer[];
}

export type RecruitKind = 'floatie' | 'boat';
/** Kit for sale in the shop: one-off purchases a team keeps for the whole match. */
export type ItemId = 'ghost';

/** Roles an admin (ADMIN_USER_IDS on the server) can hand out by Discord user id. */
export type RoleId = 'creator';
export interface RoleInfo {
  admin: boolean;
  /** May upload a custom body and head (user-generated character art). */
  creator: boolean;
}

/** What a unit can do on its turn. Move is a budget, gear takes the move, every attack ends the turn. */
export type ActionId = 'move' | WeaponId | 'skip';

/**
 * The whole match as the host sees it, for someone who joins or re-joins mid-match. The sim's
 * own snapshot type lives in the client, so it travels as `snapshot: unknown`; the event log is
 * typed because the server pays XP from it and a replacement host must carry it on.
 */
export interface MatchSync {
  seed: number;
  terrain: EncodedTerrain;
  events: MatchEvent[];
  snapshot: unknown;
}

export type PlayerInput =
  /** Pick which of your moles acts this turn (before moving or attacking). */
  | { kind: 'select'; unitId: string }
  | { kind: 'action'; action: ActionId }
  | { kind: 'stopMove' }
  | { kind: 'cancel' }
  | { kind: 'move'; dir: -1 | 0 | 1 }
  | { kind: 'jump' }
  /** Preview only, so spectators see where the thrower is aiming. */
  | { kind: 'aim'; angle: number; power: number }
  | { kind: 'fire'; angle: number; power: number }
  | { kind: 'shoot'; angle: number }
  | { kind: 'kick' }
  | { kind: 'skip' }
  /** Buy phase: add (or, with a negative count, sell back) items for the whole team. */
  | { kind: 'buy'; weapon: WeaponId; count: number }
  /** Buy phase: hire an extra mole. Floaties are cheap and stationary; boat mates stand with the team. */
  | { kind: 'recruit'; unit: RecruitKind }
  /** Buy phase: kit such as the last-shot ghost; kept for the whole match, so it can be bought once. */
  | { kind: 'buyItem'; item: ItemId }
  /** Buy phase: this player is done shopping. The round starts when every player is. */
  | { kind: 'ready' };

export type ClientAuth =
  | { kind: 'discord'; accessToken: string }
  | { kind: 'dev'; userId: string; name: string };

export type ClientMessage =
  | { t: 'hello'; instanceId: string; auth: ClientAuth; avatarUrl?: string }
  /** Admins only: grant or revoke a role for a Discord user id. The server answers with 'roles'. */
  | { t: 'setRole'; userId: string; role: RoleId; granted: boolean }
  | { t: 'listRoles' }
  | { t: 'setReady'; ready: boolean }
  | { t: 'setTeam'; team: number }
  | { t: 'setSpectator'; spectator: boolean }
  | { t: 'updateSettings'; settings: Partial<MatchSettings> }
  | { t: 'startMatch' }
  | { t: 'input'; input: PlayerInput }
  | { t: 'sim'; payload: unknown }
  /** Host → one client: the full state the server asked for with `syncRequest`. */
  | { t: 'terrainSync'; to: string; sync: MatchSync }
  /** A replica that has not received its sync yet asks the server to request one again. */
  | { t: 'requestSync' }
  /** A host that (re)loaded mid-match has no sim to run; the server hands the match to someone who does. */
  | { t: 'resignHost' }
  | { t: 'matchEnd'; events: MatchEvent[]; outcomes: PlayerOutcome[] }
  | { t: 'forfeit' }
  | { t: 'backToLobby' }
  | { t: 'equip'; kind: 'body' | 'outfit' | 'trail'; id: string }
  | { t: 'setPerks'; perks: string[] }
  | { t: 'ping' };

export interface SelfInfo {
  id: string;
  name: string;
  avatarUrl?: string;
}

export type ServerMessage =
  /** Sent after hello and after any role change: what this player may do, and who the creators are. */
  | { t: 'roles'; mine: RoleInfo; creators: { id: string; name?: string }[] }
  | { t: 'welcome'; you: SelfInfo; profile: Profile; lobby: LobbyState }
  | { t: 'lobby'; lobby: LobbyState }
  | { t: 'profile'; profile: Profile }
  | { t: 'matchStart'; match: MatchStart }
  | { t: 'input'; from: string; input: PlayerInput }
  | { t: 'forfeit'; from: string }
  | { t: 'sim'; from: string; payload: unknown }
  /** Server → host: send `for` the full match state. */
  | { t: 'syncRequest'; for: string }
  | { t: 'terrainSync'; from: string; sync: MatchSync }
  /** Server → the client that now runs the simulation; `match.hostId` is already updated. */
  | { t: 'becomeHost'; match: MatchStart }
  | { t: 'results'; rewards: Record<string, MatchRewards>; outcomes: PlayerOutcome[] }
  | { t: 'error'; code: string; message: string }
  | { t: 'pong' };

export function encode(msg: ClientMessage | ServerMessage): string {
  return JSON.stringify(msg);
}

export function decode<T>(raw: string): T | null {
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && typeof v.t === 'string' ? (v as T) : null;
  } catch {
    return null;
  }
}
